/**
 * <sock-collection-tabs>
 *
 * A tabbed strip of collections under the pack selector. Each tab shows that
 * collection's socks in a horizontal row; clicking one goes to its product page.
 *
 * The strip is hidden by CSS while a pack of two or more is being built, since
 * the bundle picker handles choosing socks there.
 */

if (!customElements.get('sock-collection-tabs')) {
  customElements.define(
    'sock-collection-tabs',
    class SockCollectionTabs extends HTMLElement {
      constructor() {
        super();

        this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));
        this.bar = this.querySelector('[data-tabs-bar]');
        this.barPrev = this.querySelector('[data-tabs-bar-prev]');
        this.barNext = this.querySelector('[data-tabs-bar-next]');

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);
        this.updateArrows = this.updateArrows.bind(this);
      }

      connectedCallback() {
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onTabKeydown);
        });

        this.barPrev?.addEventListener('click', () => this.scrollBar(-1));
        this.barNext?.addEventListener('click', () => this.scrollBar(1));
        this.bar?.addEventListener('scroll', this.updateArrows, { passive: true });

        this.querySelectorAll('[data-tabs-row]').forEach((row) => {
          row.addEventListener('scroll', this.updateArrows, { passive: true });
        });
        this.querySelectorAll('[data-tabs-row-prev]').forEach((button) => {
          button.addEventListener('click', () => this.scrollRow(button, -1));
        });
        this.querySelectorAll('[data-tabs-row-next]').forEach((button) => {
          button.addEventListener('click', () => this.scrollRow(button, 1));
        });

        window.addEventListener('resize', this.updateArrows);

        // The active panel starts hidden until its images have a size to report.
        requestAnimationFrame(this.updateArrows);
      }

      disconnectedCallback() {
        this.bar?.removeEventListener('scroll', this.updateArrows);
        window.removeEventListener('resize', this.updateArrows);
      }

      /* ------------------------------------------------------------------ */
      /* Tabs                                                                */
      /* ------------------------------------------------------------------ */

      onTabClick(event) {
        this.select(event.currentTarget.dataset.tab);
      }

      onTabKeydown(event) {
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        const step = keys[event.key];
        if (!step) return;

        event.preventDefault();
        const current = this.tabs.indexOf(event.currentTarget);
        const next = (current + step + this.tabs.length) % this.tabs.length;
        this.tabs[next].focus();
        this.select(this.tabs[next].dataset.tab);
      }

      select(index) {
        this.tabs.forEach((tab) => {
          tab.setAttribute('aria-selected', String(tab.dataset.tab === index));
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== index;
        });
        this.updateArrows();
      }

      /* ------------------------------------------------------------------ */
      /* Scrolling                                                           */
      /* ------------------------------------------------------------------ */

      scrollBar(direction) {
        if (!this.bar) return;
        this.bar.scrollBy({ left: this.bar.clientWidth * 0.6 * direction, behavior: 'smooth' });
      }

      scrollRow(button, direction) {
        const row = button.parentElement?.querySelector('[data-tabs-row]');
        if (!row) return;
        const item = row.querySelector('.sock-collection-tabs__item');
        const step = item ? item.getBoundingClientRect().width + 12 : 120;
        row.scrollBy({ left: step * 3 * direction, behavior: 'smooth' });
      }

      updateArrows() {
        this.toggleArrows(this.bar, this.barPrev, this.barNext);

        const panel = this.panels.find((item) => !item.hidden);
        if (!panel) return;

        this.toggleArrows(
          panel.querySelector('[data-tabs-row]'),
          panel.querySelector('[data-tabs-row-prev]'),
          panel.querySelector('[data-tabs-row-next]')
        );
      }

      toggleArrows(scroller, prev, next) {
        if (!scroller || !prev || !next) return;

        const { scrollLeft, scrollWidth, clientWidth } = scroller;
        const overflows = scrollWidth - clientWidth > 2;
        prev.hidden = !overflows || scrollLeft <= 2;
        next.hidden = !overflows || scrollLeft >= scrollWidth - clientWidth - 2;
      }
    }
  );
}
